import { FunctionComponent, useEffect, useMemo, useState } from "react";
import moment from "moment";
import posthog from "posthog-js";
import { useAuthStore } from "../../../../stores/auth/useAuthStore";
import { useBillingStore } from "../../../../stores/billing/useBillingStore";
import { useAppStore } from "../../../../stores/application/useAppStore";

import WhyToCancelSection from "./cancel/WhyToCancelSection";
import WhatCanWeDoBetterSection from "./cancel/WhatCanWeDoBetterSection";
import ThanksForFeedbackSection from "./cancel/ThanksForFeedbackSection";
import SubscriptionCanceledSection from "./cancel/SubscriptionCanceledSection";

export interface ICancelationFlowQuestions {
    code?: string,
    description?: string,
    ceo?: string
}

const CancelFlow: FunctionComponent = () => {
    const [section, setSection] = useState(1)
    const [state, setState] = useState<ICancelationFlowQuestions>({
        'code': '',
        'description': '',
        'ceo': ''
    })
    const { store: { subscription, user } } = useAuthStore();
    const { store: { selectedBillingPlan, selectedPrice, billingPlans } } = useBillingStore();
    const { store: { modal } } = useAppStore();

    const currentPlan = useMemo(() => {
        const fromModal = modal?.props?.currentPlan;
        if (fromModal) return String(fromModal);
        const matchedPlan = billingPlans?.find((plan: any) =>
            plan?.prices?.some((price: any) =>
                price?.productId === subscription?.productId || price?.priceId === subscription?.priceId
            )
        );
        const planName = selectedBillingPlan?.name ?? matchedPlan?.name;
        const interval = selectedPrice?.interval ?? subscription?.interval;
        if (planName && interval) {
            return `${planName} ${interval === 'year' ? 'Yearly' : 'Monthly'}`;
        }
        if (planName) return planName;
        return 'Unknown';
    }, [
        modal?.props?.currentPlan,
        selectedBillingPlan?.name,
        selectedPrice?.interval,
        subscription?.interval,
        subscription?.productId,
        subscription?.priceId,
        billingPlans,
    ]);

    const tenureDays = useMemo(() => {
        const fromModal = Number(modal?.props?.tenureDays);
        if (Number.isFinite(fromModal) && fromModal >= 0) return fromModal;
        const createdAt = (subscription as any)?.createdAt;
        if (!createdAt) return 0;
        const created = moment(createdAt);
        if (!created.isValid()) return 0;
        return Math.max(0, moment().diff(created, 'days'));
    }, [modal?.props?.tenureDays, subscription]);

    const nextSection = () => setSection(previous => previous + 1)
    const previousSection = () => setSection(previous => previous - 1)

    useEffect(() => {
        if (!user?._id) return;
        posthog.capture('cancellation flow started', {
            current_plan: currentPlan,
            tenure_days: tenureDays,
            reason_selected: 'not_selected',
        });
    }, [user?._id, currentPlan, tenureDays]);

    return (
        <>
            {section === 1 && <WhyToCancelSection nextSection={nextSection} setState={setState} />}
            {section === 2 && <WhatCanWeDoBetterSection nextSection={nextSection} previousSection={previousSection} setState={setState} />}
            {section === 3 && <ThanksForFeedbackSection nextSection={nextSection} state={state} />}
            {section === 4 && <SubscriptionCanceledSection />}
        </>
    )
}

export default CancelFlow